'use client';

import { useCallback, useState } from 'react';

export type AppTheme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'ssp-theme-preference';

function isTheme(value: string | null): value is AppTheme {
  return value === 'light' || value === 'dark';
}

export function getStoredThemePreference(): AppTheme | null {
  if (typeof window === 'undefined') return null;
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isTheme(stored) ? stored : null;
  } catch {
    return null;
  }
}

export function resolveInitialThemePreference(): AppTheme {
  const stored = getStoredThemePreference();
  if (stored) return stored;

  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function applyThemePreference(theme: AppTheme): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;

  root.classList.remove('theme-light', 'theme-dark');
  root.classList.add(theme === 'dark' ? 'theme-dark' : 'theme-light');
  root.classList.toggle('dark', theme === 'dark');
  root.style.colorScheme = theme;
}

export function persistThemePreference(theme: AppTheme): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // ignore write failures (e.g. third-party iframe storage restrictions)
  }
}

export function useThemePreference() {
  const [theme, setThemeState] = useState<AppTheme>(() => resolveInitialThemePreference());

  const setTheme = useCallback((nextTheme: AppTheme) => {
    setThemeState(nextTheme);
    applyThemePreference(nextTheme);
    persistThemePreference(nextTheme);
  }, []);

  return {
    theme,
    setTheme,
  };
}
